import { useMemo, useState } from "react"
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query"
import {
  CalendarRange,
  ChevronLeft,
  ChevronRight,
  Pencil,
  Plus,
  Trash2,
  Users,
} from "lucide-react"
import { toast } from "sonner"
import { ApplyClosureDialog } from "@/components/absences/ApplyClosureDialog"
import { ClosureRangeSheet } from "@/components/calendar/ClosureRangeSheet"
import { DayInfoPopover } from "@/components/calendar/DayInfoPopover"
import { EntryEditorSheet } from "@/components/calendar/EntryEditorSheet"
import { YearCalendarGrid } from "@/components/calendar/YearCalendarGrid"
import {
  DAY_TYPE_META,
  DAY_TYPE_ORDER,
  ENTRY_TYPE_LABEL,
} from "@/components/calendar/dayTypes"
import { LoadingSpinner } from "@/components/shared"
import { Button } from "@/components/ui/button"
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card"
import { Select } from "@/components/ui/select"
import { fmtDate } from "@/lib/format"
import { useCan } from "@/lib/permissions"
import { api, apiError } from "@/services/api"
import type { CalendarEntry, DayInfo } from "@/types"

const CURRENT_YEAR = new Date().getFullYear()
const YEAR_CHOICES = Array.from({ length: 11 }, (_, i) => CURRENT_YEAR - 3 + i)

export function CompanyCalendarPage() {
  const queryClient = useQueryClient()
  const canEdit = useCan("kalender_pflegen")
  const canApplyClosure = useCan("personal_schreiben")
  const [year, setYear] = useState(CURRENT_YEAR)
  const [popover, setPopover] = useState<{ day: DayInfo; anchor: HTMLElement } | null>(null)
  const [closureOpen, setClosureOpen] = useState(false)
  const [applyDialog, setApplyDialog] = useState<{ open: boolean; from: string; to: string }>({
    open: false,
    from: "",
    to: "",
  })
  const [entryDialog, setEntryDialog] = useState<{ open: boolean; entry: CalendarEntry | null }>({
    open: false,
    entry: null,
  })

  const yearQuery = useQuery({
    queryKey: ["calendar", "year", year],
    queryFn: () => api.calendar.year(year),
  })

  const entriesQuery = useQuery({
    queryKey: ["calendar", "entries", year],
    queryFn: () =>
      api.calendar.entries.list({ from: `${year}-01-01`, to: `${year}-12-31`, size: 100 }),
  })

  const deleteEntry = useMutation({
    mutationFn: (id: string) => api.calendar.entries.remove(id),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["calendar"] })
      toast.success("Eintrag gelöscht")
    },
    onError: (e) => toast.error(apiError(e)),
  })

  const holidays = useMemo(
    () => (yearQuery.data?.days ?? []).filter((d) => d.dayType === "FEIERTAG"),
    [yearQuery.data]
  )

  return (
    <div className="space-y-4">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <h1 className="text-2xl font-semibold tracking-tight">Betriebskalender</h1>
        <div className="flex items-center gap-2">
          <Button
            variant="outline"
            size="icon"
            aria-label="Vorheriges Jahr"
            onClick={() => setYear((y) => y - 1)}
          >
            <ChevronLeft />
          </Button>
          <Select
            aria-label="Jahr wählen"
            className="w-28"
            value={String(year)}
            onChange={(e) => setYear(Number(e.target.value))}
          >
            {(YEAR_CHOICES.includes(year) ? YEAR_CHOICES : [...YEAR_CHOICES, year].sort()).map(
              (y) => (
                <option key={y} value={y}>
                  {y}
                </option>
              )
            )}
          </Select>
          <Button
            variant="outline"
            size="icon"
            aria-label="Nächstes Jahr"
            onClick={() => setYear((y) => y + 1)}
          >
            <ChevronRight />
          </Button>
          {canEdit && (
            <>
              <Button variant="outline" onClick={() => setClosureOpen(true)}>
                <CalendarRange /> Betriebsurlaub
              </Button>
              <Button onClick={() => setEntryDialog({ open: true, entry: null })}>
                <Plus /> Eintrag
              </Button>
            </>
          )}
        </div>
      </div>

      {/* Legende */}
      <div className="flex flex-wrap items-center gap-x-4 gap-y-1 text-xs text-muted-foreground">
        {DAY_TYPE_ORDER.map((t) => (
          <span key={t} className="inline-flex items-center gap-1.5">
            <span className={`inline-block size-3 rounded ${DAY_TYPE_META[t].dotClass}`} />
            {DAY_TYPE_META[t].label}
          </span>
        ))}
      </div>

      <div className="grid grid-cols-1 gap-4 xl:grid-cols-[1fr_20rem]">
        <div>
          {yearQuery.isLoading ? (
            <LoadingSpinner />
          ) : yearQuery.isError ? (
            <Card>
              <CardContent className="py-6 text-sm text-destructive">
                Kalender konnte nicht geladen werden: {apiError(yearQuery.error)}
              </CardContent>
            </Card>
          ) : (
            <YearCalendarGrid
              days={yearQuery.data?.days ?? []}
              selectedDate={popover?.day.date ?? null}
              onDayClick={(day, anchor) =>
                setPopover((prev) => (prev?.day.date === day.date ? null : { day, anchor }))
              }
            />
          )}
        </div>

        {/* Rechtes Panel: Feiertage + Eintraege */}
        <div className="space-y-4">
          <Card>
            <CardHeader className="pb-2">
              <CardTitle className="text-sm">Feiertage {year}</CardTitle>
            </CardHeader>
            <CardContent className="pb-4">
              {yearQuery.isLoading ? (
                <LoadingSpinner />
              ) : holidays.length === 0 ? (
                <p className="text-sm text-muted-foreground">Keine Feiertage berechnet.</p>
              ) : (
                <ul className="space-y-1 text-sm">
                  {holidays.map((d) => (
                    <li key={d.date} className="flex justify-between gap-2">
                      <span className="tabular-nums text-muted-foreground">{fmtDate(d.date)}</span>
                      <span className="text-right font-medium">{d.holidayName}</span>
                    </li>
                  ))}
                </ul>
              )}
            </CardContent>
          </Card>

          <Card>
            <CardHeader className="pb-2">
              <CardTitle className="text-sm">Einträge {year}</CardTitle>
            </CardHeader>
            <CardContent className="pb-4">
              {entriesQuery.isLoading ? (
                <LoadingSpinner />
              ) : (entriesQuery.data?.data.length ?? 0) === 0 ? (
                <p className="text-sm text-muted-foreground">
                  Keine Abweichungen in diesem Jahr.
                </p>
              ) : (
                <ul className="space-y-2">
                  {entriesQuery.data?.data.map((entry) => (
                    <li key={entry.id} className="rounded-md border p-2 text-sm">
                      <div className="flex items-start justify-between gap-2">
                        <div>
                          <p className="font-medium">
                            {entry.name || ENTRY_TYPE_LABEL[entry.entryType]}
                          </p>
                          <p className="tabular-nums text-xs text-muted-foreground">
                            {fmtDate(entry.dateFrom)}
                            {entry.dateTo !== entry.dateFrom && ` – ${fmtDate(entry.dateTo)}`}
                            {" · "}
                            {ENTRY_TYPE_LABEL[entry.entryType]}
                            {entry.dayTypeOverride &&
                              ` → ${DAY_TYPE_META[entry.dayTypeOverride].label}`}
                          </p>
                          {entry.note && (
                            <p className="mt-1 text-xs text-muted-foreground">{entry.note}</p>
                          )}
                          {entry.entryType === "BETRIEBSURLAUB" && canApplyClosure && (
                            <Button
                              variant="outline"
                              size="sm"
                              className="mt-2"
                              onClick={() =>
                                setApplyDialog({
                                  open: true,
                                  from: entry.dateFrom,
                                  to: entry.dateTo,
                                })
                              }
                            >
                              <Users /> Auf Mitarbeiter anwenden
                            </Button>
                          )}
                        </div>
                        {canEdit && (
                          <div className="flex shrink-0 gap-1">
                            <Button
                              variant="ghost"
                              size="icon"
                              className="size-7"
                              aria-label="Eintrag bearbeiten"
                              onClick={() => setEntryDialog({ open: true, entry })}
                            >
                              <Pencil className="!size-3.5" />
                            </Button>
                            <Button
                              variant="ghost"
                              size="icon"
                              className="size-7 text-destructive hover:text-destructive"
                              aria-label="Eintrag löschen"
                              disabled={deleteEntry.isPending}
                              onClick={() => {
                                if (window.confirm("Eintrag wirklich löschen?")) {
                                  deleteEntry.mutate(entry.id)
                                }
                              }}
                            >
                              <Trash2 className="!size-3.5" />
                            </Button>
                          </div>
                        )}
                      </div>
                    </li>
                  ))}
                </ul>
              )}
            </CardContent>
          </Card>
        </div>
      </div>

      {popover && (
        <DayInfoPopover
          day={popover.day}
          anchor={popover.anchor}
          onClose={() => setPopover(null)}
        />
      )}
      <ClosureRangeSheet open={closureOpen} onOpenChange={setClosureOpen} />
      {applyDialog.open && (
        <ApplyClosureDialog
          open={applyDialog.open}
          onOpenChange={(open) => setApplyDialog((s) => ({ ...s, open }))}
          defaultFrom={applyDialog.from}
          defaultTo={applyDialog.to}
        />
      )}
      <EntryEditorSheet
        open={entryDialog.open}
        entry={entryDialog.entry}
        onOpenChange={(open) => setEntryDialog((s) => ({ ...s, open }))}
      />
    </div>
  )
}
